import { useCallback } from 'react';
import { useAuth } from '@/contexts/integrated-auth';
import { ChatMessageList } from './chat-message-list';
import { ChatInput } from './chat-input';
import { useSceneChat, type GifData, type TrackData, type LeagueData } from './hooks/use-scene-chat';
import { MessageCircle } from 'lucide-react';

interface SceneChatViewProps {
  sceneId: number;
  isMember: boolean;
}

export function SceneChatView({ sceneId, isMember }: SceneChatViewProps) {
  const { user, isAuthenticated } = useAuth();

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    deleteMessage,
    isSending,
  } = useSceneChat({
    sceneId,
    userId: user?.id || null,
    username: user?.username || user?.firstName || null,
    avatarUrl: user?.avatarImageUrl || null,
    isMember,
    enabled: isAuthenticated && isMember,
  });

  const handleSendMessage = useCallback(
    async (content: string) => {
      await sendMessage({ content, contentType: 'text' });
    },
    [sendMessage]
  );

  const handleSendGif = useCallback(
    async (gifData: GifData, comment?: string) => {
      await sendMessage({ content: comment || '', contentType: 'gif', gifData });
    },
    [sendMessage]
  );

  const handleSendTrack = useCallback(
    async (trackData: TrackData, comment?: string) => {
      await sendMessage({ content: comment || '', contentType: 'track', trackData });
    },
    [sendMessage]
  );

  const handleSendLeague = useCallback(
    async (leagueData: LeagueData) => {
      await sendMessage({ content: '', contentType: 'league', leagueData });
    },
    [sendMessage]
  );

  const handleDelete = useCallback(
    async (messageId: string) => {
      await deleteMessage(messageId);
    },
    [deleteMessage]
  );

  if (!isMember) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Scene Members Only</h3>
        <p className="text-muted-foreground">Join this scene to chat with other members.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <ChatMessageList
          messages={messages}
          isLoading={isLoading}
          error={error}
          currentUserId={user?.id || null}
          isOwner={false}
          onReport={() => {}}
          onDelete={handleDelete}
        />
      </div>
      <div className="px-2 pt-2 pb-2">
        <ChatInput
          onSendMessage={handleSendMessage}
          onSendGif={handleSendGif}
          onSendTrack={handleSendTrack}
          onSendLeague={handleSendLeague}
          isSending={isSending}
          disabled={!isAuthenticated}
        />
      </div>
    </div>
  );
}
